import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  ChatModerationContext,
  ChatModerationService,
  ChatModMessage
} from '../../../../../services/chat-moderation/chat-moderation.service';

@Component({
  selector: 'app-chat-mod',
  templateUrl: './chatmod.component.html',
  styleUrls: ['./chatmod.component.scss']
})
export class ChatModComponent implements OnInit, OnDestroy {
  @ViewChild('feedScroller') feedScroller: ElementRef;

  assetId = '';
  context: ChatModerationContext | null = null;
  messages: ChatModMessage[] = [];
  pinnedMessage: ChatModMessage | null = null;
  connectionState = 'offline';
  presenceCount = 0;
  channelState: any = {};
  activePoll: any = null;
  pollsEnabled = false;
  loading = false;
  newMessageCount = 0;

  activeTool: 'announce' | 'poll' | 'bans' | 'filters' = 'announce';

  announceText = '';

  pollQuestion = '';
  pollOptionDraft = '';
  pollOptions: string[] = [];
  pollResults: any = null;
  pollTimer: any = null;

  banUserId = '';
  banDuration = '5m';
  banReason = '';

  filterDraft = '';
  filterWords: string[] = [];
  slowModeEnabled = false;
  slowModeSeconds = 5;
  filtersDirty = false;

  selectedUserId = '';
  selectedUserName = '';
  showInspector = false;

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatMod: ChatModerationService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.subs.push(this.route.params.subscribe((params: Params) => {
      this.assetId = params['id'];
      this.chatMod.loadForAsset(this.assetId);
    }));

    this.subs.push(this.chatMod.context$.subscribe(ctx => {
      this.context = ctx;
    }));

    this.subs.push(this.chatMod.messages$.subscribe(messages => {
      const wasAtBottom = this.isFeedAtBottom();
      const prevCount = this.messages.length;
      this.messages = messages || [];
      if (wasAtBottom || this.messages.length <= prevCount) {
        setTimeout(() => this.scrollToBottom(), 0);
        this.newMessageCount = 0;
      } else {
        this.newMessageCount += Math.max(0, this.messages.length - prevCount);
      }
    }));

    this.subs.push(this.chatMod.pinnedMessage$.subscribe(message => {
      this.pinnedMessage = message;
    }));

    this.subs.push(this.chatMod.connectionState$.subscribe(state => {
      this.connectionState = state || 'offline';
    }));

    this.subs.push(this.chatMod.presenceCount$.subscribe(count => {
      this.presenceCount = count || 0;
    }));

    this.subs.push(this.chatMod.channelState$.subscribe(state => {
      this.channelState = state || {};
      this.applyChannelState(this.channelState);
    }));

    this.subs.push(this.chatMod.activePoll$.subscribe(poll => {
      if (!this.pollsEnabled) {
        this.activePoll = null;
        this.stopPollTimer();
        return;
      }
      this.activePoll = poll;
      this.syncPollTimer();
    }));

    this.subs.push(this.chatMod.loading$.subscribe(loading => {
      this.loading = !!loading;
    }));
  }

  ngOnDestroy() {
    for (let i = 0; i < this.subs.length; i++) this.subs[i].unsubscribe();
    this.stopPollTimer();
  }

  goBack() {
    this.router.navigate(['/asset_management/asset/add/streamconfig', this.assetId]);
  }

  setTool(tool: 'announce' | 'poll' | 'bans' | 'filters') {
    if (tool === 'poll' && !this.pollsEnabled) return;
    this.activeTool = tool;
  }

  get channelName(): string {
    return this.context ? this.context.channel : '';
  }

  get privateRoomPrefix(): string {
    return this.context ? this.context.roomPrefix : '';
  }

  get privateChatEnabled(): boolean {
    return !!(this.context && this.context.enablePrivateRooms);
  }

  get activeBans(): any[] {
    const bans = this.channelState && this.channelState.bans ? this.channelState.bans : [];
    const now = Date.now();
    return Array.isArray(bans)
      ? bans.filter(ban => ban && (!ban.expiresAt || Number(ban.expiresAt) > now))
      : [];
  }

  get connectionLabel(): string {
    if (this.connectionState === 'connected') return 'Connected';
    if (this.connectionState === 'reconnecting') return 'Reconnecting';
    return 'Offline';
  }

  connectionClass(): string {
    if (this.connectionState === 'connected') return 'connected';
    if (this.connectionState === 'reconnecting') return 'reconnecting';
    return 'offline';
  }

  postAnnouncement(pin: boolean) {
    const text = (this.announceText || '').trim();
    if (!text) {
      this.toastrService.warning('Announcement text is required');
      return;
    }
    if (text.length > 500) {
      this.toastrService.warning('Announcement cannot exceed 500 characters');
      return;
    }
    this.chatMod.postAnnouncement(text, pin).subscribe(
      (res: any) => {
        if (pin) {
          const pinned = this.pinnedMessageFromAnnouncementResponse(res, text);
          this.chatMod.pinnedMessage$.next(pinned);
          this.setLocalPinnedMessage(pinned);
        }
        this.announceText = '';
        this.toastrService.success(pin ? 'Announcement posted and pinned' : 'Announcement posted');
      },
      () => this.toastrService.warning('Unable to post announcement')
    );
  }

  pinMessage(message: ChatModMessage) {
    const pinnedMessage = Object.assign({}, message, {
      username: this.chatMod.usernameForPinnedMessage(message)
    });
    this.chatMod.pinMessage(message).subscribe(
      () => {
        this.chatMod.pinnedMessage$.next(pinnedMessage);
        this.setLocalPinnedMessage(pinnedMessage);
        this.toastrService.success('Message pinned');
      },
      () => this.toastrService.warning('Unable to pin message')
    );
  }

  unpinMessage() {
    this.chatMod.unpinMessage().subscribe(
      () => {
        this.chatMod.pinnedMessage$.next(null);
        this.setLocalPinnedMessage(null);
        this.toastrService.success('Message unpinned');
      },
      () => this.toastrService.warning('Unable to unpin message')
    );
  }

  deleteMessage(message: ChatModMessage) {
    if (!confirm('Delete this message?')) return;
    this.chatMod.deleteMessage(message).subscribe(
      () => {
        this.chatMod.markMessageDeleted(message.timetoken);
        this.toastrService.success('Message deleted');
      },
      () => this.toastrService.warning('Unable to delete message')
    );
  }

  inspectUser(message: ChatModMessage) {
    this.selectedUserId = message.userId;
    this.selectedUserName = message.username;
    this.showInspector = true;
  }

  closeInspector() {
    this.showInspector = false;
    this.selectedUserId = '';
    this.selectedUserName = '';
  }

  startBanFromMessage(message: ChatModMessage) {
    this.banUserId = message.userId;
    this.banReason = '';
    this.activeTool = 'bans';
  }

  startBanFromInspector() {
    this.banUserId = this.selectedUserId;
    this.activeTool = 'bans';
    this.closeInspector();
  }

  banUser() {
    const userId = (this.banUserId || '').trim();
    if (!userId) {
      this.toastrService.warning('User ID is required');
      return;
    }
    if (this.banDuration === 'permanent' && !(this.banReason || '').trim()) {
      this.toastrService.warning('Reason is required for permanent bans');
      return;
    }
    if (!confirm('Ban this user?')) return;

    this.chatMod.banUser(userId, this.banDuration, this.banReason).subscribe(
      (res: any) => {
        const data = res && res.data ? res.data : (res || {});
        const ban = {
          userId,
          expiresAt: data.expiresAt !== undefined ? data.expiresAt : this.localBanExpiresAt(this.banDuration),
          reason: (this.banReason || '').trim(),
          bannedAt: Date.now()
        };
        this.upsertLocalBan(ban);
        this.toastrService.success('User banned');
        this.banUserId = '';
        this.banReason = '';
      },
      () => this.toastrService.warning('Unable to ban user')
    );
  }

  unbanUser(userId: string) {
    if (!confirm('Unban this user?')) return;
    this.chatMod.unbanUser(userId).subscribe(
      () => {
        this.removeLocalBan(userId);
        this.toastrService.success('User unbanned');
      },
      () => this.toastrService.warning('Unable to unban user')
    );
  }

  addPollOption(event?: Event) {
    if (event) event.preventDefault();
    const value = (this.pollOptionDraft || '').trim();
    if (!value) return;
    if (this.pollOptions.length >= 6) {
      this.toastrService.warning('Polls support up to 6 options');
      return;
    }
    if (this.pollOptions.indexOf(value) !== -1) {
      this.pollOptionDraft = '';
      return;
    }
    this.pollOptions.push(value);
    this.pollOptionDraft = '';
  }

  removePollOption(index: number) {
    this.pollOptions.splice(index, 1);
  }

  launchPoll() {
    const question = (this.pollQuestion || '').trim();
    if (!question) {
      this.toastrService.warning('Poll question is required');
      return;
    }
    if (this.pollOptions.length < 2) {
      this.toastrService.warning('Add at least 2 poll options');
      return;
    }
    this.chatMod.launchPoll(question, this.pollOptions.slice()).subscribe(
      (res: any) => {
        this.activePoll = res && res.data ? res.data : { question, options: this.pollOptions.slice() };
        this.pollQuestion = '';
        this.pollOptions = [];
        this.toastrService.success('Poll launched');
        this.syncPollTimer();
      },
      () => this.toastrService.warning('Unable to launch poll')
    );
  }

  closePoll() {
    if (!confirm('Close the active poll?')) return;
    this.chatMod.closePoll().subscribe(
      () => {
        this.activePoll = null;
        this.pollResults = null;
        this.stopPollTimer();
        this.toastrService.success('Poll closed');
      },
      () => this.toastrService.warning('Unable to close poll')
    );
  }

  addFilterWord(event?: Event) {
    if (event) event.preventDefault();
    const value = (this.filterDraft || '').trim().toLowerCase();
    if (!value) return;
    if (this.filterWords.indexOf(value) === -1) {
      this.filterWords.push(value);
      this.filtersDirty = true;
    }
    this.filterDraft = '';
  }

  removeFilterWord(index: number) {
    this.filterWords.splice(index, 1);
    this.filtersDirty = true;
  }

  markFiltersDirty() {
    this.filtersDirty = true;
  }

  saveFilters() {
    this.chatMod.saveFilters(this.filterWords.slice(), this.slowModeEnabled, this.slowModeSeconds).subscribe(
      () => {
        this.filtersDirty = false;
        this.toastrService.success('Filters saved');
      },
      () => this.toastrService.warning('Unable to save filters')
    );
  }

  deleteAllSelectedUserMessages() {
    const userId = this.selectedUserId;
    if (!userId) return;
    const userMessages = this.getUserMessages(userId);
    if (!userMessages.length) return;
    if (!confirm('Delete all visible messages from this user?')) return;
    for (let i = 0; i < userMessages.length; i++) {
      this.chatMod.deleteMessage(userMessages[i]).subscribe(
        () => this.chatMod.markMessageDeleted(userMessages[i].timetoken),
        () => {}
      );
    }
    this.toastrService.success('Delete requests sent');
  }

  getUserMessages(userId: string): ChatModMessage[] {
    const out: ChatModMessage[] = [];
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].userId === userId) out.push(this.messages[i]);
      if (out.length >= 10) break;
    }
    return out;
  }

  userMessageCount(userId: string): number {
    let count = 0;
    for (let i = 0; i < this.messages.length; i++) {
      if (this.messages[i].userId === userId) count++;
    }
    return count;
  }

  downloadChat() {
    if (!this.messages.length) {
      this.toastrService.warning('No messages to download');
      return;
    }

    const rows = [[
      'created_at',
      'user_id',
      'username',
      'role',
      'message'
    ]];

    for (let i = 0; i < this.messages.length; i++) {
      const message = this.messages[i];
      rows.push([
        message.createdAt ? new Date(message.createdAt).toISOString() : '',
        message.userId,
        message.username,
        message.role,
        message.deleted ? '[removed by moderator]' : message.text
      ]);
    }

    const csv = rows.map(row => row.map(value => this.csvCell(value)).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'chat-' + (this.channelName || this.assetId || 'export') + '.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  initials(name: string): string {
    const value = (name || '?').trim();
    return value.charAt(0).toUpperCase();
  }

  trackByIndex(index: number): number {
    return index;
  }

  relativeTime(date: Date): string {
    const d = date ? new Date(date) : new Date();
    const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (seconds < 30) return 'just now';
    if (seconds < 60) return seconds + 's ago';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes + 'm ago';
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    return Math.floor(hours / 24) + 'd ago';
  }

  showNewMessages() {
    this.scrollToBottom();
    this.newMessageCount = 0;
  }

  onFeedScroll() {
    if (this.isFeedAtBottom()) this.newMessageCount = 0;
  }

  pollOptionPercent(option: any): number {
    const count = this.pollOptionVotes(option);
    const total = this.pollTotalVotes();
    if (!total) return 0;
    return Math.round((count / total) * 100);
  }

  pollOptionVotes(option: any): number {
    const label = this.pollOptionLabel(option);
    const results = this.pollResults || this.activePoll || {};
    if (results.counts && results.counts[label] !== undefined) return Number(results.counts[label]) || 0;
    if (option && option.votes !== undefined) return Number(option.votes) || 0;
    return 0;
  }

  pollOptionLabel(option: any): string {
    if (typeof option === 'string') return option;
    return (option && (option.label || option.text || option.name || option.value)) || '';
  }

  pollOptionsForDisplay(): any[] {
    const poll = this.pollResults || this.activePoll;
    if (!poll) return [];
    if (Array.isArray(poll.options)) return poll.options;
    return [];
  }

  pollTotalVotes(): number {
    const results = this.pollResults || this.activePoll || {};
    if (results.totalVotes !== undefined) return Number(results.totalVotes) || 0;
    const opts = this.pollOptionsForDisplay();
    let total = 0;
    for (let i = 0; i < opts.length; i++) total += this.pollOptionVotes(opts[i]);
    return total;
  }

  private applyChannelState(state: any) {
    if (!state) return;
    if (Array.isArray(state.filterWords) && !this.filtersDirty) {
      this.filterWords = state.filterWords.slice();
    }
    if (state.slowMode && !this.filtersDirty) {
      this.slowModeEnabled = !!state.slowMode.enabled;
      this.slowModeSeconds = state.slowMode.seconds || this.slowModeSeconds;
    }
    if (this.pollsEnabled && state.activePoll) this.activePoll = state.activePoll;
  }

  private syncPollTimer() {
    this.stopPollTimer();
    if (!this.activePoll) return;
    this.refreshPollResults();
    this.pollTimer = setInterval(() => this.refreshPollResults(), 3000);
  }

  private refreshPollResults() {
    if (!this.activePoll || !this.channelName) return;
    this.chatMod.pollResults().subscribe(
      (res: any) => {
        this.pollResults = res && res.data ? res.data : res;
      },
      () => {}
    );
  }

  private stopPollTimer() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private upsertLocalBan(ban: any) {
    const bans = this.activeBans.filter(item => String(item.userId) !== String(ban.userId));
    bans.push(ban);
    this.setLocalBans(bans);
  }

  private removeLocalBan(userId: string) {
    this.setLocalBans(this.activeBans.filter(ban => String(ban.userId) !== String(userId)));
  }

  private setLocalBans(bans: any[]) {
    const nextState = Object.assign({}, this.channelState || {}, { bans });
    this.channelState = nextState;
    this.chatMod.channelState$.next(nextState);
  }

  private setLocalPinnedMessage(message: ChatModMessage | null) {
    const nextState = Object.assign({}, this.channelState || {}, { pinnedMessage: message });
    this.channelState = nextState;
    this.chatMod.channelState$.next(nextState);
  }

  private pinnedMessageFromAnnouncementResponse(res: any, fallbackText: string): ChatModMessage {
    const data = res && res.data ? res.data : (res || {});
    const raw = data.message || data;
    const meta = raw.meta || {};
    const user = raw.user || raw.author || {};
    const timetoken = (raw.timetoken || data.timetoken || Date.now()).toString();
    const userId = (raw.userId || user.id || meta.userId || 'moderator').toString();
    const username = (raw.username || user.name || meta.name || userId).toString();

    return {
      timetoken,
      userId,
      username,
      text: (raw.text || raw.message || raw.body || fallbackText || '').toString(),
      createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
      role: (raw.role || meta.role || 'moderator').toString(),
      deleted: false,
      raw
    };
  }

  private localBanExpiresAt(duration: string): number | null {
    const value = (duration || '').trim();
    if (value === 'permanent') return null;
    const amount = Number(value.slice(0, -1));
    const unit = value.slice(-1);
    if (!amount) return Date.now();
    if (unit === 'm') return Date.now() + amount * 60 * 1000;
    if (unit === 'h') return Date.now() + amount * 60 * 60 * 1000;
    if (unit === 'd') return Date.now() + amount * 24 * 60 * 60 * 1000;
    return Date.now();
  }

  private csvCell(value: any): string {
    const text = value === undefined || value === null ? '' : String(value);
    return '"' + text.replace(/"/g, '""') + '"';
  }

  private isFeedAtBottom(): boolean {
    const el = this.feedScroller && this.feedScroller.nativeElement ? this.feedScroller.nativeElement : null;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  private scrollToBottom() {
    const el = this.feedScroller && this.feedScroller.nativeElement ? this.feedScroller.nativeElement : null;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
}
